import { datadogLogs, LogsEvent, StatusType } from '@datadog/browser-logs';
import { datadogRum, RumEvent } from '@datadog/browser-rum';

import packageInfo from '../../package.json';

let isDatadogInitialized = false;

/**
 * What Session Replay may record of the page, and what click action names may be built from.
 * One setting drives both: `enablePrivacyForActionName` (on below) makes the SDK name an action
 * from the element text only when that text would be recorded in a replay.
 *
 * `mask-user-input` masked form inputs and nothing else, so replays recorded every visible
 * word (the patient's first name in the Home greeting, medication names and doses on order cards,
 * chat text) and the SDK named click actions after them. `mask` replaces all text with `x` and
 * names a click "Masked Element" unless the element or an ancestor carries `data-dd-action-name`.
 *
 * The trade-off is real and it is a product and compliance call, which is why it is a constant:
 * a replay under `mask` shows layout, clicks, scrolls and navigation but no copy, and an action
 * with no `data-dd-action-name` is told apart by its selector instead of its label. The ways to
 * win some of that back without recording PHI are annotating the controls that matter with
 * `data-dd-action-name`, or `mask-unless-allowlisted` once an allowlist of static UI copy is
 * supplied through `window.$DD_ALLOW` (the SDK ships no loader for it, so today it would behave
 * exactly like `mask`). Do not relax this to `mask-user-input` or `allow` without that decision.
 *
 * Check before release: nothing in src carries `data-dd-action-name` today, so at rollout every
 * click action is named "Masked Element", and any Datadog monitor, dashboard, saved view or funnel
 * keyed on `@action.name` goes flat without an error. Find those first, and give the controls
 * they depend on a static `data-dd-action-name` (in this change or a linked follow-up).
 */
const DATADOG_PRIVACY_LEVEL = 'mask';

/**
 * Session Replay is off (it was 100, every session). A replay stores the raw page URL when the
 * recording starts and again at every new view, in its `Meta` record, and `beforeSend` never sees
 * a replay record, so it cannot rewrite that URL. While a credential can arrive in a landing link
 * (`accessToken`, which App.tsx reads and never removes) and Messages addresses a conversation by
 * `chatId`, a replay would store both as they are. Turn it back on once those values are out of
 * the page URL before `initDatadog()` runs (SECVM-678). RUM sessions, and so errors, resources,
 * actions and views, are still collected at `sessionSampleRate`.
 */
const DATADOG_SESSION_REPLAY_SAMPLE_RATE = 0;

/**
 * Query parameters whose value is a credential: it logs someone in, or proves who they are.
 *
 * The portal accepts `accessToken` on any route (App.tsx) and writes `accessToken` and
 * `refreshToken` into its own hand-off links (CommonBanner, AskToUpgradeTo, Home, useDeepLinks).
 * `token` is the magic link and the password-reset link, `login-token` is staff signing in as a
 * patient, `restoreToken`, `partnerToken` and `finishSignupToken` resume a signup (useOnboarding),
 * and `vt` is the click-to-verify link token (pages/VerifyIdentity/utils/verifyLinkToken). The
 * OAuth names and the AWS signing parameters are not used by the portal today; they cover a
 * provider redirect and a presigned attachment URL (services/channels) that RUM records as a
 * resource, and cost nothing when absent. `Signature`, `Policy` and `Key-Pair-Id` are the same
 * for a CloudFront signed URL, which the images policy allows from any `*.cloudfront.net` host.
 * `impersonation_code` is the login service's staff impersonation landing (`#impersonation_code=`
 * on any route once the service is on, LMD-682) and `state` is the anti-forgery value a provider
 * redirect returns beside `code`; both land in the address bar before telemetry starts.
 */
const CREDENTIAL_PARAMS = [
  'accessToken',
  'refreshToken',
  'token',
  'login-token',
  'restoreToken',
  'partnerToken',
  'finishSignupToken',
  'vt',
  'code',
  'impersonation_code',
  'state',
  'id_token',
  'access_token',
  'refresh_token',
  'X-Amz-Signature',
  'X-Amz-Security-Token',
  'X-Amz-Credential',
  'Signature',
  'Policy',
  'Key-Pair-Id'
];

/**
 * Query parameters whose value is PHI or personal data.
 *
 * `message` can carry text a patient typed. Nothing in the portal writes it into its own URLs
 * any more (every prefill of the support-request form travels in memory,
 * pages/Messages/supportRequestDraft), but an inbound NewSupportRequest deep link still arrives as
 * `?action=…&message=…`, so the landing URL carries it until the link resolves. `aiThreadId` and
 * `chatId` are chat ids, and chat ids count as PHI (LMD-1821). The portal keeps `aiThreadId` out
 * of its own URLs since Sept 28 (hooks/useSelectedAiThreadId), but a link made before that still
 * lands with it; `chatId` is how Messages addresses a conversation. `email` is the sign-up and
 * verify-identity prefill. `medication` is the post-onboarding agreement step's pick. `metrics` is
 * the weight or blood pressure reading a dashboard deep link carries. `answer`, `a` and `sab` are
 * clinical answers the sign-up steps keep in the URL (age, gallstones, diabetes, pregnancy, sex at
 * birth, insurance provider).
 *
 * The portal's own API requests reach Datadog as well: RUM records every fetch and XHR as a
 * resource with its full URL (`trackResources`), and a failed one puts it in the error message
 * and in the Logs `http.url`. Some of those URLs are built from what the patient typed or the
 * browser reported, and they are geographic identifiers tied to `usr.id`: `search` is the home
 * address in the autocomplete, sent on every debounced keystroke (services/lookup, services/auth);
 * `address`, `currentLatitude`, `currentLongitude`, `latitude` and `longitude` are the pharmacy
 * search, the last four straight from `navigator.geolocation` (services/pharmacies); `zipCode` is
 * the lab search on `/auth/labs` (services/signup); `preferCities` narrows the address check on
 * `/lookup/verify-addresses` to the cities the patient is in (services/lookup), and goes out as
 * `preferCities[]`; `preferredMedication` is the medication the patient picked, sent to
 * `/required-agreements` (services/general). Their values are plain text, not JWTs, so only the
 * name list protects them.
 *
 * Left alone on purpose: step keys and flags (`s`, `step`, `subStep`, `hideProgress`, `action`,
 * `tab`, `type`, `program`), marketing parameters, and the opaque record ids (`orderId`, `callId`,
 * `apptId`, `surveyId`, `resultId`, `ppID`, `ppid`, `newPPID`, `userId`, `brazeExternalId`,
 * `staffId`). The ids resolve to nothing without a session, they are what an engineer needs to
 * correlate an incident, and `usr.id` carries the same patient id already. `resourcePath` is an
 * encoded destination path, not a credential.
 */
const PHI_PARAMS = [
  'message',
  'aiThreadId',
  'chatId',
  'email',
  'medication',
  'metrics',
  'answer',
  'a',
  'sab',
  'search',
  'address',
  'currentLatitude',
  'currentLongitude',
  'latitude',
  'longitude',
  'zipCode',
  'preferCities',
  'preferredMedication'
];

/**
 * Every parameter above, in the query string and in the fragment.
 *
 * The privacy level masks DOM text, not URLs, and the SDK copies `location.href` into
 * `view.url` and the next view's `view.referrer` as-is. Redacting these values in `beforeSend`
 * keeps them out; PostHog runs the same redaction (hooks/useAppAnalytics).
 *
 * The fragment gets the whole list, not just `vt`. The fragment is the shape the verify link is
 * sent in, not the only shape a credential arrives in: a link rewriter can percent-encode the
 * `#`, a templating mistake can emit `?` where `#` was meant, and an OAuth provider returns its
 * tokens in the fragment. A credential that reaches the query string is already in the access log,
 * which is all the more reason not to let it into RUM and PostHog as well.
 *
 * `vt` is the click-to-verify link's token (pages/VerifyIdentity/utils/verifyLinkToken): a
 * single-use credential, redeemable for a few minutes, whose payload names the patient. It rides
 * in the fragment precisely so that it never reaches a server, which buys nothing if the
 * browser-side SDKs ship it to Datadog and PostHog instead, and they do. The fragment used to be
 * passed through here untouched, on the reasoning that a fragment never leaves the client: true
 * of our servers, false of our vendors (LMD-2273 review).
 *
 * `src/index.tsx` takes the verify token's fragment out of the address bar before either SDK
 * starts. This is the second layer, for whatever the first one cannot reach.
 *
 * What this does NOT reach is Session Replay's `Meta` record. It carries `window.location.href`
 * as it is when the recording starts and again at every new view (startFullSnapshots in
 * browser-rum 7.11), and `beforeSend` never sees a replay record. So any credential or PHI
 * parameter that is in the address bar at that moment is stored in the replay as it is: the
 * `accessToken` that App.tsx reads and never removes, and every PHI parameter the portal writes
 * into its own links (`chatId` on each Messages conversation, the sign-up answers, `medication`,
 * `email`). Closing it takes one of: strip credentials and keep PHI out of the query before
 * `initDatadog()` (the way `captureVerifyLinkToken` hands the verify token off), or start
 * recording manually (`startSessionReplayRecordingManually`) around the routes that carry them.
 * Until one of those lands, replay is off (DATADOG_SESSION_REPLAY_SAMPLE_RATE, SECVM-678).
 */
const SENSITIVE_QUERY_PARAMS = [...CREDENTIAL_PARAMS, ...PHI_PARAMS];
const SENSITIVE_FRAGMENT_PARAMS = SENSITIVE_QUERY_PARAMS;
const REDACTED_QUERY_VALUE = 'REDACTED';

/**
 * The names a value is matched by when it is not in a URL: a key of a log's or RUM event's
 * context, and a JSON key quoted in text. The patterns only know URLs, tokens and addresses; an
 * opaque chat id, or a typed address, under a key that names it matches none of them.
 * `handleRequestCatch`, `useMifStepSubmit` and `useCreateAppointment` spread whole server error
 * bodies into the context, and those can have any field name.
 *
 * Added here: the names a chat thread, stream or conversation id goes by in this codebase. The AI
 * chat telemetry logs them as `threadRef` and `streamRef`, masked to the last four characters,
 * and a masked value is let through (MASKED_REF); a whole id under any of them is not.
 *
 * Taken out: `message`, so that a log message and a server's error message stay readable (their
 * text still goes through the patterns), and `code`, which in a context or a JSON body is a
 * machine-readable error code (`ERR_NETWORK`, `ECONNABORTED`) that monitors facet on. The OAuth
 * `code` only ever arrives in a URL, where it is still redacted.
 */
const SENSITIVE_KEY_NAMES = [
  ...SENSITIVE_QUERY_PARAMS,
  'threadId',
  'streamId',
  'threadRef',
  'streamRef',
  'conversationId'
].filter((name) => name !== 'message' && name !== 'code');
const SENSITIVE_CONTEXT_KEYS = new Set(SENSITIVE_KEY_NAMES.map((name) => name.toLowerCase()));

// An id masked to `***` and its last four characters at most (maskChatId in the AI chat
// telemetry, maskId on the Convex side): enough to line up one chat's steps, too little to find it.
const MASKED_REF = /^\*{3}[A-Za-z0-9_-]{0,4}$/;

const escapeRegExp = (source: string) => source.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const anyOf = (names: string[]) => names.map(escapeRegExp).join('|');

// `?`, `&` and `#` as they arrive once the page address has been percent-encoded into another
// parameter's value, one level deep and two levels deep (`dl=…` and `dr=…` on a beacon).
const ENCODED_SEPARATOR = '%23|%2523|%26|%2526|%3F|%253F';
const ENCODED_EQUALS = '%3D|%253D';

// A value can also sit URL-encoded inside another parameter's value: an analytics beacon carries
// the page address that way (`dl=https%3A%2F%2F…%3FaiThreadId%3D…`), and Datadog records the
// beacon as a resource. The encoded name and value find it. The name has to follow a separator,
// so that `a` cannot fire on the tail of some other parameter's name.
//
// The value runs to the next separator, plain or encoded, and it cannot tell a separator from an
// `&` or `#` inside a free-text value: `message=I have Q&A about my meds` stops after `Q`, and the
// rest is left in. So it is the fallback, not the main pass. A parameter that is known to carry a
// URL (`dl`, `dr`, `redirectTo`, URL_VALUED_PARAMS below) is decoded and parsed exactly by
// `redactNestedUrlParams` before this runs; this pattern is for a URL under a name nobody listed
// and for text that quotes one.
const NESTED_PHI_PATTERN = new RegExp(
  `((?:${ENCODED_SEPARATOR})(?:${anyOf(PHI_PARAMS)})(?:${ENCODED_EQUALS}))(?:(?!%(?:25)?(?:26|23))[^&#\\s"'<>])+`,
  'gi'
);

// The same shape for the credentials, which a beacon carries as `…%23vt%3D<jwt>`. Anchored on the
// separator immediately before the name, so it cannot fire on some other parameter whose name
// merely ends in `vt`; the value charset is base64url, the JWT's dots, and the three characters
// (`+`, `/`, `=`) a standard base64 token adds, which a beacon has percent-encoded, perhaps twice.
// A value that stops at the first other character is deliberate: a token mangled on the way here
// is what the query-string pass is for.
// `?` and its encodings are in the separator group for the same reason `vt` is a query param
// above: the mangled link shapes put the token after a `?` rather than a `#`.
const NESTED_CREDENTIAL_PATTERN = new RegExp(
  `((?:${ENCODED_SEPARATOR}|[#&?])(?:${anyOf(CREDENTIAL_PARAMS)})(?:${ENCODED_EQUALS}|=))(?:[A-Za-z0-9._~+/=-]|%(?:25)*(?:2B|2F|3D))+`,
  'gi'
);

// Free text is not a URL: an error message that quotes a request URL, or a deep link logged as a
// string (`action=…&key=…`, useActionDeepLink), has no query string for the pass below to parse.
// This is the plain `name=value` shape for every listed name, credentials included: the pattern
// above needs a separator before the name, so it misses one at the very start of the string, and
// `login-token` or the OAuth `code` is opaque, not a JWT. An array parameter is serialized as
// `name[]` (utils/services). It is kept out of URL redaction on purpose: a `?` inside a fragment
// belongs to the fragment, and a URL is better served by the exact pass.
const PLAIN_PARAM_PATTERN = new RegExp(
  `((?:^|[?&#;])(?:${anyOf(SENSITIVE_QUERY_PARAMS)})(?:\\[\\]|%5B%5D)?=)[^\\s&#"'<>]+`,
  'gi'
);

// The same names as JSON keys inside text. RUM collects `console.error` arguments into the error
// message with objects JSON-stringified, and a server body can be quoted in an error the same
// way. A string or number value is replaced; a masked ref is kept, as it is in a context. Each
// alternative of the value can only advance, so the scan stays linear.
const JSON_FIELD_PATTERN = new RegExp(
  `("(?:${anyOf(SENSITIVE_KEY_NAMES)})"\\s*:\\s*)("(?:[^"\\\\]|\\\\.)*"|-?[0-9][0-9.eE+-]*)`,
  'gi'
);

const redactJsonField = (match: string, key: string, value: string) =>
  value.startsWith('"') && MASKED_REF.test(value.slice(1, -1))
    ? match
    : `${key}"${REDACTED_QUERY_VALUE}"`;

// Every token this app handles is a JWT: three base64url segments, the first starting `eyJ`
// (`{"` encoded). Matching the shape catches one under a parameter name nobody listed, in a
// message, a stack, an `Authorization` header that an error captured, or a path segment.
//
// A match starts only where a token can start: at the start of the text, after a character a
// token cannot contain, or after a percent-encoded byte (`%3DeyJ…` in a beacon). Without that,
// a long run of `eyJ` with no dot is quadratic, every `eyJ` scanning to the end of the run, and
// `view.url` comes from whatever link the patient followed. The boundary is captured, so a
// replacement keeps it with `$1`. A lookbehind would not need that, but it is a syntax error in
// Safari before 16.4, and the production browserslist still includes iOS Safari 15.6; one there
// would stop this module from loading.
const JWT_PATTERN =
  /(^|[^A-Za-z0-9_-]|%(?:25)*[0-9A-Fa-f]{2})eyJ[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]*/g;

// An address is personal data wherever it shows up: a prefill that the portal does not know to
// list, a server error that echoes the account, a `user@host` in a URL. Bounded quantifiers keep
// the scan linear on a long run of word characters, and the guard below skips the scan for the
// common case of a string without an at sign.
const EMAIL_DOMAIN = '[A-Za-z0-9-]{1,63}(?:\\.[A-Za-z0-9-]{1,63}){0,8}\\.[A-Za-z]{2,24}';
const EMAIL_PATTERN = new RegExp(`[A-Za-z0-9._%+-]{1,64}@${EMAIL_DOMAIN}`, 'g');

// The same address with its at sign percent-encoded, which is how every URL the portal builds
// through `URLSearchParams` or `encodeURIComponent` carries it (`%40`), and an analytics beacon
// carries it once more (`%2540`, and `%252540` a level deeper). The local part cannot contain `%`
// here, or it would swallow the encoded separators in front of it
// (`…%3Fcontact%3Dpatient%2540…`); the group before it keeps the separator, or the encoded byte
// such as `%3D`, out of the match so that it stays in the output.
const ENCODED_EMAIL_PATTERN = new RegExp(
  `(^|[^A-Za-z0-9._%+-]|%(?:25)*[0-9A-F]{2})((?:[A-Za-z0-9._+-]|%(?:25)*2B){1,64})%(?:25)*40${EMAIL_DOMAIN}`,
  'gi'
);
const MAY_CONTAIN_EMAIL = /@|%(?:25)*40/i;

/** The replacements that need no knowledge of where in a string the value sits. */
const redactTokensAndNestedValues = (text: string): string => {
  const redacted = text
    .replace(JWT_PATTERN, `$1${REDACTED_QUERY_VALUE}`)
    .replace(NESTED_CREDENTIAL_PATTERN, `$1${REDACTED_QUERY_VALUE}`)
    .replace(NESTED_PHI_PATTERN, `$1${REDACTED_QUERY_VALUE}`);

  return MAY_CONTAIN_EMAIL.test(redacted)
    ? redacted
        .replace(EMAIL_PATTERN, REDACTED_QUERY_VALUE)
        .replace(ENCODED_EMAIL_PATTERN, `$1${REDACTED_QUERY_VALUE}`)
    : redacted;
};

/**
 * Rewrites `source`, a query string or a fragment without its leading `?`/`#`, with every named
 * parameter's value replaced by a fixed marker. Returns `null` when none of them are present, so
 * the caller can hand back the original text rather than a `URLSearchParams` re-encoding of it.
 * Names match without regard to case: the portal reads them exactly, but a link that was
 * lower-cased or upper-cased on the way is still a link carrying the credential. An array
 * parameter's `[]` suffix (utils/services serializes arrays that way) and an HTML-escaped
 * separator left in front of a name (`&amp;chatId=…`, parsed as the key `amp;chatId`) do not hide
 * the name either.
 */
const redactParams = (source: string, names: string[]): string | null => {
  const params = new URLSearchParams(source);
  const sensitive = new Set(names.map((name) => name.toLowerCase()));
  const present = Array.from(new Set(params.keys())).filter((key) =>
    sensitive.has(key.toLowerCase().replace(/^amp;/, '').replace(/\[\]$/, ''))
  );
  if (!present.length) return null;

  present.forEach((key) => params.set(key, REDACTED_QUERY_VALUE));

  return params.toString();
};

const locateQuery = (url: string) => {
  const hashStart = url.indexOf('#');
  const queryEnd = hashStart === -1 ? url.length : hashStart;
  // Searched in the pre-fragment part only: a `?` that appears solely inside the fragment belongs
  // to the fragment and is not the start of a query string.
  const queryStart = url.slice(0, queryEnd).indexOf('?');

  return { hashStart, queryEnd, queryStart };
};

// Parameters whose value is itself a URL: the page address and referrer an analytics beacon
// carries (`dl` and `dr` on Google's, `rl` on Meta's), a tracker's `url` and `referrer`, and the
// destination of a redirect. Case does not matter, as everywhere else in this file.
const URL_VALUED_PARAMS = new Set(
  ['dl', 'dr', 'rl', 'url', 'referrer', 'redirectTo', 'redirect'].map((name) => name.toLowerCase())
);

// How many URLs deep (and how many layers of percent-encoding) the exact pass follows. A beacon
// is two deep; the limit only keeps a hostile or broken value from making it recurse.
const MAX_NESTED_URL_DEPTH = 4;

const decodeOnce = (text: string): string | null => {
  try {
    return decodeURIComponent(text);
  } catch {
    // A stray `%` that is not an escape. Left to the patterns.
    return null;
  }
};

// A URL that was percent-encoded one more time than the parameter it sits in (a beacon's `dr`
// after `dl`'s level): nothing raw is left that could separate a query, only the encoded `?`,
// `&` or `#`.
const isEncodedUrl = (value: string) => !/[?&#]/.test(value) && /%(?:25)*(?:3F|23|26)/i.test(value);

/**
 * The exact pass for a parameter that holds a URL: decode it, redact it as the URL it is, and
 * encode it back. Unlike the patterns, this parses the nested query string, so a free-text value
 * with an encoded `&` in it (`message=I have Q&A about my meds`) is one value, not two. Returns
 * `value` itself when nothing in it needed redacting, so that an untouched beacon is not
 * re-encoded.
 */
function redactNestedUrl(value: string, depth: number): string {
  if (depth >= MAX_NESTED_URL_DEPTH) return value;

  if (isEncodedUrl(value)) {
    const decoded = decodeOnce(value);
    if (decoded === null) return value;

    const redacted = redactNestedUrl(decoded, depth + 1);

    return redacted === decoded ? value : encodeURIComponent(redacted);
  }

  return redactUrl(value, depth + 1);
}

/** Runs `redactNestedUrl` on each URL-valued query parameter of `url`, keeping order and repeats. */
const redactNestedUrlParams = (url: string, depth: number): string => {
  const { queryEnd, queryStart } = locateQuery(url);
  if (queryStart === -1 || depth >= MAX_NESTED_URL_DEPTH) return url;

  let changed = false;
  const params = new URLSearchParams();
  new URLSearchParams(url.slice(queryStart + 1, queryEnd)).forEach((value, key) => {
    const redacted = URL_VALUED_PARAMS.has(key.toLowerCase())
      ? redactNestedUrl(value, depth)
      : value;
    if (redacted !== value) changed = true;
    params.append(key, redacted);
  });

  return changed
    ? `${url.slice(0, queryStart + 1)}${params.toString()}${url.slice(queryEnd)}`
    : url;
};

function redactUrl(rawUrl: string, depth: number): string {
  // The nested URLs go first, while their separators can still be told from a value's own; the
  // patterns after them mop up what the exact pass has no name for.
  const url = redactTokensAndNestedValues(redactNestedUrlParams(rawUrl, depth));

  const { hashStart, queryEnd, queryStart } = locateQuery(url);

  let result = url;

  // A route inside the fragment with its own query (`#/x?chatId=…`) parses as one key, `/x?chatId`,
  // so a PHI value there is kept; a credential is still caught by the patterns above. The portal
  // has no hash router, so it never builds such a URL.
  if (hashStart !== -1) {
    const fragment = redactParams(url.slice(hashStart + 1), SENSITIVE_FRAGMENT_PARAMS);
    if (fragment !== null) result = `${url.slice(0, hashStart + 1)}${fragment}`;
  }

  if (queryStart !== -1) {
    const query = redactParams(result.slice(queryStart + 1, queryEnd), SENSITIVE_QUERY_PARAMS);
    if (query !== null) {
      result = `${result.slice(0, queryStart + 1)}${query}${result.slice(queryEnd)}`;
    }
  }

  return result;
}

/**
 * Replaces the value of every sensitive query *and fragment* parameter in `url` with a fixed
 * marker, leaving the rest of the URL intact. Works on absolute and relative URLs alike, and on a
 * URL carried inside another one (an analytics beacon's `dl`).
 *
 * The nesting depth is not a parameter here, so that a `.map(redactSensitiveUrlParams)` cannot
 * pass an array index into it.
 */
const redactSensitiveUrlParams = (rawUrl: string): string => redactUrl(rawUrl, 0);

/**
 * The same redaction for text that is not a URL but may quote one: an error message, a stack, a
 * log line, a string in a log's context. It never re-parses or re-encodes the text, it only
 * replaces the sensitive values it recognises, so it is safe on any string.
 */
const redactSensitiveText = (text: string): string =>
  redactTokensAndNestedValues(text)
    .replace(PLAIN_PARAM_PATTERN, `$1${REDACTED_QUERY_VALUE}`)
    .replace(JSON_FIELD_PATTERN, redactJsonField);

const redactOptionalText = (text: string | undefined) => (text ? redactSensitiveText(text) : text);

type ViewUrlTelemetry = { name?: string; referrer?: string; url: string };

/** Both SDKs allow `view.url` / `view.referrer` to be rewritten in `beforeSend`; do it in place. */
const redactViewUrls = (view: ViewUrlTelemetry | undefined) => {
  if (!view) return;

  view.url = redactSensitiveUrlParams(view.url);
  if (view.referrer) view.referrer = redactSensitiveUrlParams(view.referrer);
  // A view's name is the developer's to set, and a `startView` call that built one from the
  // route would carry the query string with it.
  if (view.name) view.name = redactSensitiveText(view.name);
};

// A log's context and a RUM event's context are free-form, and `logDataDogEvent` callers put
// whole RTK Query errors, deep links and API responses in them. Bounded so that a pathological
// object cannot make the redaction the slow part of logging. The bound cuts the walk, not the
// redaction: an object or array at that depth is replaced whole, never sent unread, since a
// listed key or a free-text field further down would otherwise leave as it is. A string there is
// still redacted like any other.
const MAX_CONTEXT_DEPTH = 8;

// A context is matched by key (SENSITIVE_CONTEXT_KEYS) as well as by what its strings look like.
// A flag, an absent value or a masked ref says nothing about the patient, and a boolean is not
// worth the lost shape; anything else under a sensitive key (a string, a number such as a
// coordinate, or a whole object such as a metrics reading) is replaced outright.
const isSensitiveEntry = (key: string, value: unknown) =>
  SENSITIVE_CONTEXT_KEYS.has(key.toLowerCase()) &&
  value !== null &&
  value !== undefined &&
  typeof value !== 'boolean' &&
  !(typeof value === 'string' && MASKED_REF.test(value));

const redactDeep = (value: unknown, depth = 0): unknown => {
  if (typeof value === 'string') return redactSensitiveText(value);
  if (!value || typeof value !== 'object') return value;
  if (depth >= MAX_CONTEXT_DEPTH) return REDACTED_QUERY_VALUE;

  const container = value as Record<string, unknown>;
  Object.keys(container).forEach((key) => {
    container[key] = isSensitiveEntry(key, container[key])
      ? REDACTED_QUERY_VALUE
      : redactDeep(container[key], depth + 1);
  });

  return container;
};

/**
 * Every field of a RUM event that can carry a URL, a credential or text from the page, limited to
 * the ones `beforeSend` is allowed to change: the SDK clones the event, hands the clone over and
 * copies back only an allowlist of paths (`MODIFIABLE_FIELD_PATHS_BY_EVENT` in rum-core 7.11), so
 * an edit anywhere else is silently dropped. Those paths are `view.url`, `view.referrer`,
 * `view.name`, `context`, `error.message`, `error.stack`, `error.handling_stack`,
 * `error.resource.url`, `error.fingerprint`, `resource.url`, `long_task.scripts[].source_url`,
 * `long_task.scripts[].invoker`, `view.performance.lcp.resource_url` and `action.target.name`.
 *
 * 7.11 also allows `resource.graphql.variables`, `resource.request.headers`,
 * `resource.response.headers` and `resource.websocket.*`. They are filled only by options this
 * init does not set (`allowedGraphQlUrls`, `trackResourceHeaders`, `betaTrackWebSockets`); turning
 * one on means adding its field here.
 *
 * `error.causes[]` is not on that list, so a cause's message cannot be redacted here.
 * `action.target.name` is left to the SDK's own privacy level (DATADOG_PRIVACY_LEVEL) rather than
 * rewritten: it is already the fixed placeholder, or a name a developer set on purpose.
 */
const redactRumEvent = (event: RumEvent) => {
  redactViewUrls(event.view);

  if (event.context) event.context = redactDeep(event.context) as typeof event.context;

  // The page's own requests, analytics beacons included, carry the page address in their URLs.
  if (event.type === 'resource') event.resource.url = redactSensitiveUrlParams(event.resource.url);

  if (event.type === 'view') {
    const lcp = event.view.performance?.lcp;
    // The largest contentful paint is often an image, and an image of a patient's own document is
    // fetched from a presigned URL.
    if (lcp?.resource_url) lcp.resource_url = redactSensitiveUrlParams(lcp.resource_url);
  }

  if (event.type === 'error') {
    const { error } = event;

    // A network error's message quotes the request, `Fetch error GET <url>`, and its resource
    // carries the URL on its own.
    if (error.message) error.message = redactSensitiveText(error.message);
    error.stack = redactOptionalText(error.stack);
    error.handling_stack = redactOptionalText(error.handling_stack);
    error.fingerprint = redactOptionalText(error.fingerprint);
    if (error.resource) error.resource.url = redactSensitiveUrlParams(error.resource.url);
  }

  // The script of a long animation frame is the document itself for inline code, so its source
  // URL is the page address with its query string, and the invoker can name one as well.
  if (event.type === 'long_task') {
    event.long_task.scripts?.forEach((script) => {
      if (script.source_url) script.source_url = redactSensitiveUrlParams(script.source_url);
      if (script.invoker) script.invoker = redactSensitiveText(script.invoker);
    });
  }
};

/**
 * The Logs SDK hands `beforeSend` the live event with no allowlist of editable fields, and a log
 * is the one place free text and arbitrary objects arrive: `message`, `error.*`, the `http.url`
 * of a network error forwarded by `forwardErrorsToLogs`, and every key of the context a
 * `logDataDogEvent` caller passed. All of it is walked, down to MAX_CONTEXT_DEPTH; below that an
 * object is replaced.
 *
 * Except a network error's `error.stack`, which is not walked but replaced. For a 5xx response or
 * a request that never completed, `forwardErrorsToLogs` puts up to 32 KiB of the response body
 * there (networkErrorCollection in browser-logs 7.11): the server's own JSON, from our API, Convex,
 * the AI chat stream or a third party, with patient fields under any key. The patterns know URLs,
 * tokens and addresses, not a name or an address in a JSON body, so the body is not sent at all.
 * The message (`Fetch error GET <url>`), `http.status_code` and `http.url` still say which request
 * failed.
 */
const NETWORK_ERROR_STACK = 'response body not collected';

const redactLogEvent = (log: LogsEvent) => {
  if (log.origin === 'network' && log.error) log.error.stack = NETWORK_ERROR_STACK;

  redactViewUrls(log.view);
  if (log.http?.url) log.http.url = redactSensitiveUrlParams(log.http.url);

  Object.keys(log).forEach((key) => {
    // Already URL-redacted above; a second, text-shaped pass over them would only disagree.
    if (key === 'view' || key === 'http') return;
    // The context a `logDataDogEvent` caller passed is merged into the log's own attributes, so
    // its keys are these keys.
    log[key] = isSensitiveEntry(key, log[key]) ? REDACTED_QUERY_VALUE : redactDeep(log[key]);
  });
};

// Redaction is a security control and the SDKs fail open: both wrap `beforeSend` so that a throw
// is swallowed and the event goes out carrying whatever was edited before the throw
// (`catchUserErrors` in browser-core 7.11). A context with a throwing getter is enough to do it.
// So a failure here must cost the event, not leak it. The one exception is a RUM view, which
// cannot be dropped and is sent with only its paths and an empty context (below).

/** Origin and path only: no query string or fragment, and no token if one is a path segment. */
const withoutQueryOrFragment = (url: string) =>
  typeof url === 'string'
    ? url.split(/[?#]/, 1)[0].replace(JWT_PATTERN, `$1${REDACTED_QUERY_VALUE}`)
    : url;

/**
 * Redacts a RUM event and says whether it may be sent. When the redaction throws, every event but
 * a view is dropped. RUM does not allow `beforeSend` to dismiss a view ("Can't dismiss view
 * events using beforeSend!"), so a view is sent with what could hold a secret cut back to the
 * path: its URL, referrer, name and largest-contentful-paint resource, and its context emptied.
 */
const redactRumEventOrDrop = (event: RumEvent): boolean => {
  try {
    redactRumEvent(event);

    return true;
  } catch {
    if (event.type !== 'view') return false;

    const { view } = event;
    view.url = withoutQueryOrFragment(view.url);
    if (view.referrer) view.referrer = withoutQueryOrFragment(view.referrer);
    if (view.name) view.name = withoutQueryOrFragment(view.name);
    const lcp = view.performance?.lcp;
    if (lcp?.resource_url) lcp.resource_url = withoutQueryOrFragment(lcp.resource_url);
    event.context = {};

    return true;
  }
};

/** Redacts a log and says whether it may be sent. A log can be dropped, so a failure drops it. */
const redactLogEventOrDrop = (log: LogsEvent): boolean => {
  try {
    redactLogEvent(log);

    return true;
  } catch {
    return false;
  }
};

const initDatadog = () => {
  if (isDatadogInitialized || import.meta.env.VITE_ENV === 'local') return;

  const applicationId = import.meta.env.VITE_DATA_DOG_APP_ID;
  const clientToken = import.meta.env.VITE_DATA_DOG_CLIENT_TOKEN;

  if (!applicationId || !clientToken) return;

  const baseUrl = import.meta.env.VITE_BASE_URL;
  const allowedTracingUrls = baseUrl ? [baseUrl] : [];

  datadogRum.init({
    allowedTracingUrls,
    applicationId,
    beforeSend: (event) => {
      if (!redactRumEventOrDrop(event)) return false;

      if (event.type !== 'error') return true;

      const source = `${event.error?.message ?? ''} ${event.error?.stack ?? ''}`;

      if (source.includes('node_modules')) return false;
      // Filter out noisy browser event cancel errors that are safe to ignore.
      // We primarily key on "IgnoredEventCancel" to avoid being overly strict
      // about the exact message/stack formatting across browsers.
      if (source.includes('IgnoredEventCancel')) return false;
      // Zoom SDK: benign when user leaves preview before video starts
      if (source.includes('VideoNotStartedError')) return false;
      return true;
    },
    clientToken,
    defaultPrivacyLevel: DATADOG_PRIVACY_LEVEL,
    // On by default in 7.11, and set anyway: it is what ties click action names to the privacy
    // level above, so it must not depend on a default that a later SDK version could change.
    enablePrivacyForActionName: true,
    env: import.meta.env.VITE_ENV,
    excludedActivityUrls: [/analytics/, /perfalytics/, /facebook/, /google/, /tiktok/, /pixel/],
    service: 'patient_fe',
    sessionReplaySampleRate: DATADOG_SESSION_REPLAY_SAMPLE_RATE,
    sessionSampleRate: 100,
    site: 'datadoghq.com',
    traceSampleRate: 20,
    trackLongTasks: true,
    trackResources: true,
    trackUserInteractions: true,
    version: packageInfo.version
  });

  datadogLogs.init({
    beforeSend: redactLogEventOrDrop,
    clientToken,
    env: import.meta.env.VITE_ENV,
    // forwardConsoleLogs: ['error'],
    forwardErrorsToLogs: true,
    service: 'patient_fe',
    site: 'datadoghq.com',
    version: packageInfo.version
  });

  isDatadogInitialized = true;
};

const logDataDogEvent = ({
  context,
  error,
  level = 'info',
  message
}: {
  context?: object;
  error?: Error;
  level?: StatusType;
  message: string;
}) => {
  datadogLogs.logger[level](message, { ...context, isHandle: true }, error);
};

export { initDatadog, logDataDogEvent, redactSensitiveText, redactSensitiveUrlParams };
